import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { SelectModule } from 'primeng/select';
import { firstValueFrom, forkJoin } from 'rxjs';
import { isStringEmpty } from '../../common/functions';
import { notifyAndTrackError } from '../../common/error';
import { AppInitService } from '../../services/app-init.service';
import { AppInsightsService } from '../../services/app-insights.service';
import { AppUserService, IAppUser, IUserSettings } from '../../services/insecta';
import { OrderService } from '../../services/insecta/order.service';
import { ProcessingComponent } from '../processing/processing.component';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

@Component({
  selector: 'app-app-user-settings',
  standalone: true,
  imports: [
    ProcessingComponent,
    CommonModule,
    IconFieldModule,
    ButtonModule,
    DialogModule,
    FloatLabelModule,
    SelectModule,
    FormsModule,
    ToggleSwitchModule
  ],
  templateUrl: './app-user-settings.component.html'
})
export class AppUserSettingsComponent implements OnChanges {
  private readonly orderService = inject(OrderService);
  private readonly msgService = inject(MessageService);
  private readonly appInsights = inject(AppInsightsService);
  private readonly appUserService = inject(AppUserService);
  private readonly appInit = inject(AppInitService);

  @Input() appUser!: IAppUser;
  userSetttingsJson: IUserSettings = {
    'Default Site': '',
    useCommonName: false,
    useSiteCode: false
  };
  sites: any[] = [];
  selectedDefaultSite = '';
  dialogHeader = 'Default Site';
  useCommonName = false;
  useSiteCode = false;

  pageVars: any = {
    currentUser: ``,
    isEditing: false,
    isReady: true,
    showUnsavedChangesDialog: false,
    pageId: `AppUser.UserSettings`
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appUser'] && this.appUser) {
      if (isStringEmpty(this.appUser.userSettingsJson) || this.appUser.userSettingsJson === '{}') {
        this.onEditUserSettings();
        this.dialogHeader = 'Please Add Default Site';
      } else {
        this.userSetttingsJson = JSON.parse(this.appUser.userSettingsJson) as IUserSettings;
        this.useCommonName = this.userSetttingsJson.useCommonName ?? false;
        this.useSiteCode = this.userSetttingsJson.useSiteCode ?? false;
        this.selectedDefaultSite = this.userSetttingsJson['Default Site'] ?? '';
      }
    }
  }

  getSites() {
    this.pageVars.isReady = false;
    const request = {
      locations: this.orderService.getLocationsForHierarchy()
    };

    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.sites = response.locations.map((location) => ({
          label: location.name,
          value: location.name
        }));
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'AppUserSettingsComponent', action: 'LoadSites:Error' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  onCancel() {
    this.pageVars.isEditing = false;
  }

  onEditUserSettings() {
    this.pageVars.isEditing = true;
    this.getSites();
  }
  onToggleChange() {
    this.saveToggleSettings();
  }

  saveToggleSettings() {
    this.appUser.userSettingsJson = JSON.stringify({
      'Default Site': this.userSetttingsJson['Default Site'] || this.selectedDefaultSite,
      useCommonName: this.useCommonName,
      useSiteCode: this.useSiteCode
    });

    this.pageVars.isReady = false;

    firstValueFrom(this.appUserService.updateAppUserSettings({ userSettingsJson: this.appUser.userSettingsJson }))
      .then(() => {
        this.appInit.appUser = this.appUser;
        this.userSetttingsJson = JSON.parse(this.appUser.userSettingsJson);
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'AppUserSettingsComponent', action: 'SaveToggleSettings:Error' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }
  onSaveUserSettings() {
    this.appUser.userSettingsJson = JSON.stringify({
      'Default Site': this.selectedDefaultSite,
      useCommonName: this.useCommonName,
      useSiteCode: this.useSiteCode
    });
    this.pageVars.isReady = false;

    firstValueFrom(this.appUserService.updateAppUserSettings({ userSettingsJson: this.appUser.userSettingsJson }))
      .then(() => {
        this.appInit.appUser = this.appUser;
        this.userSetttingsJson = JSON.parse(this.appUser.userSettingsJson);

        this.pageVars.isEditing = false;
        this.dialogHeader = 'Default Site';
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'AppUserSettingsComponent', action: 'SaveUserSettings:Error' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }
}
